import { type JSX, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import {
  ContentContainer,
  ContentHeader,
  ContentScroll,
  PageTitle,
} from "features/layout/components";
import { LeadPreviewSidebar } from "features/leads/components";

import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import Loader from "common/components/Loader";
import RenderIf from "common/components/RenderIf";

import { useCampaignContext } from "../../../context/CampaignContext";
import useAudienceStats from "../../../datahooks/useAudienceStats";
import {
  getSortedAndFilteredFlows,
  isValidSchedule,
  validateFlows,
} from "../../../utils";
import { CampaignInfo } from "../CampaignInfo";
import TargetingPreview from "../TargetingPreview";
import Audience from "./Audience";
import AudienceInfo from "./AudienceInfo";
import CampaignFlows from "./CampaignFlows";
import CampaignSchedule from "./CampaignSchedule";
import ResetTargetingButton from "./ResetTargettingButton";
import Review from "./Review";
import Targeting from "./Targeting";

export default function CreateCampaign() {
  const { pathname } = useLocation();

  const [previewLeadId, setPreviewLeadId] = useState<string>();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const {
    flows,
    inmail_optimization: isInMailOptimizationEnabled,
    state,
    schedule: { timezone: _timezone, ...campaignSchedule },
    labels,
  } = useCampaignContext();

  const {
    audienceStats,
    isLoadingAudienceStats,
    isAudienceStatsError,
    refetchAudienceStats,
  } = useAudienceStats();

  useEffect(() => {
    setIsPreviewOpen(false);
  }, [pathname]);

  if (state !== "INIT") return <Navigate to="../details" replace />;

  let mainContent: JSX.Element;

  if (isAudienceStatsError) {
    mainContent = (
      <ErrorPlaceholder
        errorMessage="Could not display campaign details"
        onRetry={refetchAudienceStats}
      />
    );
  } else if (isLoadingAudienceStats) {
    mainContent = <Loader />;
  } else {
    const sortedAndFilteredFlows = getSortedAndFilteredFlows(
      flows,
      isInMailOptimizationEnabled,
    );

    const STEPS = [
      {
        path: "targeting",
        component: (
          <Targeting
            openLeadPreview={(leadId) => {
              setPreviewLeadId(leadId);
              setIsPreviewOpen(true);
            }}
          />
        ),
      },
      {
        path: "audience",
        component: (
          <Audience
            audienceStats={audienceStats}
            openLeadPreview={(leadId) => {
              //TODO: REVERT WHEN BACK END FIXES THEIR TYPES
              setPreviewLeadId(leadId.toString());
              setIsPreviewOpen(true);
            }}
          />
        ),
      },
      {
        path: "flows",
        component: <CampaignFlows audienceStats={audienceStats} />,
      },
      {
        path: "schedule",
        component: <CampaignSchedule flows={sortedAndFilteredFlows} />,
      },
      {
        path: "review",
        component: (
          <Review flows={sortedAndFilteredFlows} autoLabels={labels} />
        ),
      },
    ];

    const { isFlowListValid } = validateFlows(
      flows,
      isInMailOptimizationEnabled,
    );

    const selectedIndex = STEPS.findIndex(({ path }) =>
      pathname.endsWith(path),
    );

    if (selectedIndex !== 0 && audienceStats.state === "PENDING") {
      return <Navigate to="targeting" replace />;
    }

    if (
      (selectedIndex === 0 || selectedIndex === -1) &&
      (audienceStats.state === "STARTED" || audienceStats.state === "COMPLETED")
    ) {
      return <Navigate to="audience" replace />;
    }

    if (selectedIndex > 2 && !isFlowListValid) {
      return <Navigate to="flows" replace />;
    }

    if (selectedIndex > 3 && !isValidSchedule(campaignSchedule)) {
      return <Navigate to="schedule" replace />;
    }

    mainContent = (
      <>
        <RenderIf condition={selectedIndex > 0}>
          <TargetingPreview
            actionComponent={
              <ResetTargetingButton
                isDisabled={
                  audienceStats ? audienceStats.progress !== 100 : true
                }
              />
            }
          />
        </RenderIf>

        <RenderIf condition={selectedIndex > 1}>
          <AudienceInfo />
        </RenderIf>

        <Routes>
          {STEPS.map(({ path, component }) => (
            <Route key={path} path={path} element={component} />
          ))}
        </Routes>
      </>
    );
  }

  return (
    <div className="flex gap-x-2 h-full grow min-h-0">
      <ContentContainer className="relative">
        <ContentHeader>
          <PageTitle>Create campaign flow</PageTitle>
        </ContentHeader>

        <ContentScroll className="px-4 pt-4 gap-y-4 grow mb-28 lg:mb-0">
          <CampaignInfo />

          {mainContent}
        </ContentScroll>
      </ContentContainer>

      <LeadPreviewSidebar
        initialState={previewLeadId}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />
    </div>
  );
}
